const activeRequests = new WeakMap();

async function cartRequest(fetcher, url, options, readJson = false) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 15000);
  try {
    const response = await fetcher(url, { ...options, signal: controller.signal });
    const data = readJson && response.ok ? await response.json() : null;
    return { ok: response.ok, status: response.status, data };
  } finally { clearTimeout(timer); }
}

export function cartRootPath(candidate = '/') {
  return typeof candidate === 'string' && /^\/(?!\/)[^?#\\\u0000-\u0020]*$/.test(candidate)
    ? `${candidate.replace(/\/+$/, '')}/`
    : '/';
}

export function readNightSelection(selects) {
  if (selects.length !== 3) return null;
  const quantities = new Map();
  let cents = 0;
  for (const select of selects) {
    const option = select.options[select.selectedIndex];
    if (!option || option.disabled || !/^\d+$/.test(option.value) || !/^\d+$/.test(option.dataset.price || '')) return null;
    const id = Number(option.value), price = Number(option.dataset.price);
    if (!Number.isSafeInteger(id) || id < 1 || !Number.isSafeInteger(price) || price < 0) return null;
    cents += price;
    quantities.set(option.value, (quantities.get(option.value) || 0) + 1);
  }
  if (!Number.isSafeInteger(cents)) return null;
  return { cents, items: [...quantities].map(([id, quantity]) => ({ id, quantity })) };
}

export function initNightBuilder(section, fetcher = globalThis.fetch) {
  const doc = section.ownerDocument;
  const form = section.querySelector('[data-night-builder-form]');
  const selects = [...section.querySelectorAll('[data-night-select]')];
  const button = section.querySelector('[data-night-submit]');
  const total = section.querySelector('[data-night-total]');
  const status = section.querySelector('[data-night-status]');
  const review = section.querySelector('[data-night-review]');
  if (!form || !button || !total || selects.length !== 3) return () => {};
  let alive = true;
  let formatter;
  const currency = section.dataset.currency || 'CHF';
  const language = section.dataset.locale || doc.documentElement.lang || 'de-CH';
  const locale = currency === 'CHF' && !language.includes('-') ? `${language}-CH` : language;
  try { formatter = new Intl.NumberFormat(locale, { style: 'currency', currency, currencyDisplay: 'code' }); }
  catch { return () => {}; }
  const text = key => section.dataset[key] || '';
  const ordering = () => doc.body.dataset.ordering === 'true';
  const say = message => { if (alive && status) status.textContent = message; };
  const pending = () => activeRequests.get(doc)?.pending === true;
  function update() {
    const selection = readNightSelection(selects);
    if (selection) total.textContent = formatter.format(selection.cents / 100);
    const state = form.dataset.nightState;
    button.disabled = !ordering() || !selection || pending() || state === 'added' || state === 'review';
    button.textContent = !ordering() ? text('preview') : state === 'pending' ? text('adding') : state === 'added' ? text('added') : text('add');
    for (const select of selects) select.disabled = pending();
    form.toggleAttribute('aria-busy', state === 'pending');
    if (state === 'pending') form.setAttribute('aria-busy', 'true');
    if (review) review.hidden = state !== 'review';
  }
  function updateProduct(select) {
    const option = select.options[select.selectedIndex], choice = select.closest('[data-night-choice]');
    if (!option || !choice) return;
    const link = choice.querySelector('[data-night-product]');
    const image = choice.querySelector('[data-night-image]');
    const unit = choice.querySelector('[data-night-unit]');
    if (link) {
      link.textContent = option.dataset.title || option.textContent;
      try {
        const url = new URL(option.dataset.productUrl || '', doc.location.href);
        if (url.origin === doc.location.origin && ['http:', 'https:'].includes(url.protocol)) link.href = url.href;
        else link.removeAttribute('href');
      } catch { link.removeAttribute('href'); }
    }
    if (image) {
      image.hidden = true;
      if (option.dataset.image) {
        try {
          const url = new URL(option.dataset.image, doc.location.href);
          if (['http:', 'https:'].includes(url.protocol)) { image.src = url.href; image.hidden = false; }
        } catch { image.hidden = true; }
      }
    }
    if (unit) unit.textContent = option.dataset.unit || '';
  }
  const onState = () => { if (alive) update(); };
  const broadcast = () => doc.dispatchEvent(new CustomEvent('latebite:builder-state'));
  async function refreshCart(path) {
    let cart = null;
    try {
      const response = await cartRequest(fetcher, `${path}cart.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin', cache: 'no-store' }, true);
      if (response.ok) {
        const result = response.data;
        if (Number.isSafeInteger(result?.item_count) && result.item_count >= 0) {
          cart = result;
          doc.querySelectorAll('[data-cart-count]').forEach(count => { count.textContent = String(cart.item_count); });
        }
      }
    } catch { cart = null; }
    doc.dispatchEvent(new CustomEvent('latebite:cart-refresh', { detail: { cart, source: 'night-builder' } }));
  }
  function onChange(event) {
    if (!selects.includes(event.target) || pending()) return;
    if (form.dataset.nightState === 'added') { delete form.dataset.nightState; say(''); }
    updateProduct(event.target);
    update();
  }
  async function onSubmit(event) {
    event.preventDefault();
    if (pending() || form.dataset.nightState === 'added' || form.dataset.nightState === 'review') return;
    if (!ordering()) { say(text('preview')); return; }
    const selection = readNightSelection(selects);
    if (!selection || !form.reportValidity()) { say(text('invalid')); return; }
    const request = { pending: true };
    activeRequests.set(doc, request);
    form.dataset.nightState = 'pending';
    say(text('adding'));
    broadcast();
    const path = cartRootPath(doc.body.dataset.cartRoot);
    let accepted = false, stockChanged = false;
    try {
      doc.dispatchEvent(new CustomEvent('latebite:cart-dirty', { detail: { source: 'night-builder' } }));
      const response = await cartRequest(fetcher, `${path}cart/add.js`, {
        method: 'POST', headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        credentials: 'same-origin', body: JSON.stringify({ items: selection.items }),
      });
      accepted = response.ok;
      stockChanged = response.status === 422;
    } catch { accepted = false; }
    form.dataset.nightState = accepted ? 'added' : 'review';
    say(text(accepted ? 'added' : stockChanged ? 'stock' : 'review'));
    await refreshCart(path);
    request.pending = false;
    broadcast();
  }
  selects.forEach(updateProduct);
  update();
  section.hidden = false;
  form.addEventListener('submit', onSubmit);
  form.addEventListener('change', onChange);
  doc.addEventListener('latebite:builder-state', onState);
  return () => {
    alive = false;
    form.removeEventListener('submit', onSubmit);
    form.removeEventListener('change', onChange);
    doc.removeEventListener('latebite:builder-state', onState);
  };
}

const initialized = new WeakMap();
function boot(root = document) {
  const sections = [...root.querySelectorAll('[data-night-builder]')];
  if (root.matches?.('[data-night-builder]')) sections.unshift(root);
  for (const section of sections) {
    if (!initialized.has(section)) initialized.set(section, initNightBuilder(section));
  }
}
if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot(), { once: true });
  else boot();
  document.addEventListener('shopify:section:load', event => boot(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('[data-night-builder]').forEach(section => { initialized.get(section)?.(); initialized.delete(section); });
  });
}
